"use client";

import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  Input,
} from "@/ui";
import { useOnboarding } from "@/lib/onboarding/OnboardingContext";
import { normaliseDomain } from "@/lib/onboarding/normaliseDomain";
import styles from "./ConfirmDomainDialog.module.css";

/**
 * Domain confirmation for a re-run of automated onboarding.
 *
 * Everything the research infers hangs off one guess — the domain — which is
 * derived from an email address and is often wrong for recruiters on a personal
 * or group address. A re-run is the point where they can say so, so it asks
 * rather than silently repeating the original guess.
 *
 * Mounted by the dashboard layout alongside the other onboarding dialogs, not
 * by the Settings modal that opens it: Settings closes as this opens (the run
 * reports progress in the status bar behind it), which would unmount a dialog
 * rendered inside it.
 */
export default function ConfirmDomainDialog() {
  const { domainPromptOpen, closeDomainPrompt, researchDomain, runAgain } =
    useOnboarding();

  const [domain, setDomain] = useState(researchDomain);
  const [error, setError] = useState<string | null>(null);

  // Reopening after a cancel should offer the default again, not the abandoned
  // edit. Keyed on the open flag so an edit survives re-renders while it's open.
  const [wasOpen, setWasOpen] = useState(domainPromptOpen);
  if (domainPromptOpen !== wasOpen) {
    setWasOpen(domainPromptOpen);
    if (domainPromptOpen) {
      setDomain(researchDomain);
      setError(null);
    }
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const normalised = normaliseDomain(domain);
    if (!normalised) {
      setError("Enter a website domain, for example yourbusiness.com");
      return;
    }

    runAgain(normalised);
  };

  return (
    <Dialog
      open={domainPromptOpen}
      onOpenChange={(open: boolean) => {
        if (!open) closeDomainPrompt();
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="sm">
          {/* The submit button lives in the footer, outside the body, so the
              form wraps the whole dialog to keep Enter-to-submit working. */}
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Please confirm your business domain</DialogTitle>
            </DialogHeader>

            <DialogBody>
              <p className={styles.intro}>
                We read this website to work out what your business does and how
                it writes, then suggest branding, style and outreach defaults.
                You&apos;ll review everything before it&apos;s applied.
              </p>

              {error && (
                <div className={styles.error}>
                  <Alert variant="error">{error}</Alert>
                </div>
              )}

              <Input
                label="Website domain"
                placeholder="yourbusiness.com"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value);
                  setError(null);
                }}
                autoFocus
                fullWidth
              />

              <p className={styles.hint}>
                This is used for the research only — it doesn&apos;t change your
                login or work email.
              </p>
            </DialogBody>

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={closeDomainPrompt}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!domain.trim()}>
                Run onboarding
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
